import { Button, Icon, PageHeader } from '@lmc/ui-kit'
import { filter, find } from 'lodash'
import PropTypes from 'prop-types'
import React, { useEffect } from 'react'
import { isBrowser, isTablet } from 'react-device-detect'
import { connect } from 'react-redux'
import Actions from '../../../../../actions'
import { Spinner } from '../../../../../components/generic'
import Operations from '../../../../../operations'
import Selectors from '../../../../../selectors'
import { history } from '../../../../../store'

import { withRouter } from '../../../../../withRouter'
import {
  EMARInitialAssessmentEmptyState,
  EMARScreenEmptyState
} from '../components'
import StockSection from './StockSection'

const isDesktop = isBrowser || isTablet

const renderContent = (props) => {
  const { medications, resident, stockItem, testId } = props
  if (!medications)
    return (
      <Spinner
        data-testid={
          testId ? `${testId}-emar-stock-loading` : 'emar-stock-loading'
        }
      />
    )
  if (!medications.length) {
    return (
      <EMARScreenEmptyState emarOnboarded resident={resident} testId={testId} />
    )
  }
  const icon = stockItem && stockItem.icon
  return (
    <div
      data-testid={
        testId ? `${testId}-emar-stock-content` : 'emar-stock-content'
      }
    >
      <StockSection
        title="Scheduled"
        residentId={resident.id}
        icon={icon}
        medications={filter(medications || [], { PRN: false })}
        testId={testId}
      />
      <StockSection
        title="Not Scheduled (PRN)"
        residentId={resident.id}
        icon={icon}
        medications={filter(medications || [], { PRN: true })}
        testId={testId}
      />
    </div>
  )
}

export function EMARResidentStockScreen(props) {
  const { fetchData, fetchLogs, home, openModal, resident, user, testId } =
    props

  const residentId = resident.id

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (residentId) {
      fetchData(residentId)
      fetchLogs(residentId)
    }
  }, [residentId])

  const residentName = `${resident.name.first} ${resident.name.last}`
  const isInitialAssessment =
    resident.status === 'pre-assessment' || resident.isActiveInitialAssessment

  return (
    <div
      style={styles.wrapper}
      data-testid={
        testId
          ? `${testId}-emar-resident-stock-screen`
          : 'emar-resident-stock-screen'
      }
    >
      <div
        style={styles.headerContainer}
        data-testid={
          testId
            ? `${testId}-emar-stock-header-container`
            : 'emar-stock-header-container'
        }
      >
        <PageHeader
          title={`${residentName}'s Medication Stock`}
          sticky
          onBack={!isDesktop ? () => history.back?.() : undefined}
          data-testid={
            testId ? `${testId}-emar-stock-header` : 'emar-stock-header'
          }
        >
          {user && user.medicationPermission && !isInitialAssessment && (
            <Button
              id="add-medication-button"
              onClick={() => openModal(resident.id)}
              style={styles.addMedicationButton}
              variant="primary"
              icon={<Icon name="PlusOutlined" />}
              disabled={home && !home.features?.emarOnboarded?.enabled}
              data-testid={
                testId
                  ? `${testId}-emar-stock-add-medication-btn`
                  : 'emar-stock-add-medication-btn'
              }
            >
              Add Medication
            </Button>
          )}
        </PageHeader>
        {isInitialAssessment ? (
          <EMARInitialAssessmentEmptyState
            preferredPeopleNominative={home.preferredPeopleNominative}
            resident={resident}
            testId={testId}
          />
        ) : (
          renderContent({ ...props, testId })
        )}
        {/* <div style={styles.helpLinkContainer}>
          <HelpLink supportLink="https://support.logmycare.co.uk/en/collections/2749236-emar" />
        </div> */}
      </div>
    </div>
  )
}

EMARResidentStockScreen.propTypes = {
  fetchData: PropTypes.func.isRequired,
  fetchLogs: PropTypes.func.isRequired,
  home: PropTypes.object.isRequired,
  medications: PropTypes.array.isRequired,
  openModal: PropTypes.func.isRequired,
  resident: PropTypes.object.isRequired,
  stockItem: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  testId: PropTypes.string
}

const styles = {
  helpLinkContainer: {
    marginTop: 24
  },
  headerContainer: {},
  helpLink: {
    marginTop: 36
  },
  wrapper: {
    padding: '0px 0px 48px',
    margin: '0px auto',
    maxWidth: 1080
  }
}

const mapStateToProps = (state, props) => ({
  home: state.global.home,
  logs: Selectors.populateLogs(state),
  medications: Selectors.getResidentStockCount(state, {
    resident: props.router.params.id
  }),
  stockItem: find(state.data['log-category-items'], {
    itemId: 'emarStockCount'
  }),
  resident: Selectors.getResidentProfile(state, {
    id: props.router.params.id
  }),
  stockLogs: filter(state.data.logs || [], { name: 'eMAR Stock Count' }),
  user: Selectors.getCurrentUserProfile(state)
})

const mapDispatchToProps = (dispatch) => ({
  fetchData: (resident) => {
    dispatch(
      Operations.loadList('emar-resident-medications', {
        resident,
        stock: 'true'
      })
    )
    dispatch(Operations.loadList('emar-round-timings'))
  },
  fetchLogs: (resident) => dispatch(Operations.loadList('logs', { resident })),
  openModal: (resident) => {
    dispatch(Operations.openModal({ modalId: 'addEmarMedication' }))
    dispatch(
      Actions.addForm({
        formId: 'addEmarMedication',
        initial: { resident }
      })
    )
  }
})

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(EMARResidentStockScreen)
)
